import { useEffect, useMemo, useRef } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@clerk/clerk-react"
import { mockClient } from "../api/mockClient"
import { type SendContactMessagePayload } from "../api/messages"
import type { ContactMessage, MessageChannel } from "../types"
import { useDemoMode } from "../demo/DemoProvider"

type ContactMessageKey = ["contactMessages", { contactId: string }]

function buildQueryKey(contactId: string): ContactMessageKey {
  return ["contactMessages", { contactId }]
}

let optimisticSequence = -1

function mergeMessages(existing: ContactMessage[], incoming: ContactMessage[]): ContactMessage[] {
  if (!incoming.length) return existing
  const map = new Map<number, ContactMessage>()
  for (const message of existing) {
    map.set(message.id, message)
  }
  for (const message of incoming) {
    map.set(message.id, message)
  }
  return [...map.values()].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
}

export function useContactMessages(contactId: string | null | undefined) {
  const enabled = Boolean(contactId)
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const latestRef = useRef<string | null>(null)

  useEffect(() => {
    latestRef.current = null
    if (!contactId) return
    queryClient.removeQueries({ queryKey: buildQueryKey(contactId), exact: true })
  }, [contactId, queryClient])

  const key = useMemo(() => (contactId ? buildQueryKey(contactId) : null), [contactId])

  const query = useQuery<ContactMessage[]>({
    queryKey: key ?? ["contactMessages", { contactId: "none" }],
    enabled,
    refetchInterval: enabled ? 5000 : false,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      if (!contactId) return []
      const token = demo ? null : await getToken()
      const since = latestRef.current
      const response = await mockClient.getContactMessages(demo, contactId, { since }, token)
      const previous = (queryClient.getQueryData<ContactMessage[]>(buildQueryKey(contactId)) ?? []).slice()
      const merged = mergeMessages(previous, response.messages)
      latestRef.current = merged.length ? merged[merged.length - 1].created_at : since
      return merged
    },
    initialData: [] as ContactMessage[],
  })

  return query
}

type SendContactMessageInput = {
  contactId: string
  body: string
  channel: MessageChannel
  metadata?: Record<string, unknown>
}

export function useSendContactMessage() {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ contactId, body, channel, metadata }: SendContactMessageInput) => {
      const payload: SendContactMessagePayload = { body, channel, metadata }
      const token = demo ? null : await getToken()
      return mockClient.sendContactMessage(demo, contactId, payload, token)
    },
    onMutate: async ({ contactId, body, channel, metadata }) => {
      const key = buildQueryKey(contactId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ContactMessage[]>(key) ?? []
      const optimisticMessage: ContactMessage = {
        id: optimisticSequence--,
        contact_id: Number(contactId.replace(/\D/g, "")) || 0,
        tenant_id: 0,
        direction: "outbound",
        origin: "web",
        channel,
        body,
        metadata: metadata ?? {},
        delivery_status: "queued",
        request_id: undefined,
        created_at: new Date().toISOString(),
      }
      queryClient.setQueryData<ContactMessage[]>(key, [...previous, optimisticMessage])
      return { previous, optimisticId: optimisticMessage.id, contactId }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      const key = buildQueryKey(context.contactId)
      queryClient.setQueryData<ContactMessage[]>(key, context.previous)
    },
    onSuccess: (response, { contactId }, context) => {
      const key = buildQueryKey(contactId)
      const current = queryClient.getQueryData<ContactMessage[]>(key) ?? []
      const resolved = response.message
      const next = current.map((msg) => (msg.id === context?.optimisticId ? resolved : msg))
      if (!next.some((msg) => msg.id === resolved.id)) {
        next.push(resolved)
      }
      next.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      queryClient.setQueryData<ContactMessage[]>(key, next)
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: buildQueryKey(variables.contactId) })
    },
  })
}
